import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import allRoutes from "../../client/constants/allRoutes";
import vistaarLogo from "assets/img/vistaar-logo.svg";

const PublicFooter = () => {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const isValidEmail = (val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSubscribing(true);
    try {
      const apiBase = (process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1/admin/auth")
        .replace(/\/admin\/auth$/, "");
      const res = await fetch(`${apiBase}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "footer" }),
      });
      const result = await res.json();
      if (result?.rescode === 1) {
        toast.success(result.message || "Subscribed successfully!");
        setEmail("");
      } else {
        toast.error(result?.message || "Could not subscribe. Please try again.");
      }
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="footer">
      {/* Gradient top border */}
      <div className="footer-gradient-border"></div>

      {/* Mini CTA strip */}
      <div className="footer-cta-strip">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-slate-900 m-0">Ready to digitize your clinic?</h3>
            <p className="text-sm text-slate-500 m-0 mt-1">Start your 14-day free trial — no credit card required.</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("openAuthModal", { detail: { tab: "signup", plan: "free" } }))}
              className="btn-primary btn-sm"
            >
              Start Free Trial
            </button>
            <a href="#pricing" className="text-sm font-semibold text-slate-600 hover:text-slate-900 transition whitespace-nowrap">
              View Pricing
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-10 mb-12">

          {/* Brand + Newsletter */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4" aria-label="Vistaar">
              <div className="h-12 w-12 flex-shrink-0">
                <img src={vistaarLogo} alt="" width="48" height="48" className="w-full h-full object-contain" />
              </div>
              <span className="brand-wordmark">Vistaar</span>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed max-w-sm">
              Smart clinic management platform for modern healthcare providers.
            </p>

            {/* Newsletter signup */}
            <div className="mt-6 max-w-sm">
              <p className="text-sm font-bold text-slate-800 m-0">Stay Updated</p>
              <p className="text-xs text-slate-500 m-0 mt-1">Get clinic tips & product updates in your inbox.</p>
              <form onSubmit={handleSubscribe} className="footer-newsletter mt-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="footer-newsletter-input"
                  aria-label="Email address"
                />
                <button type="submit" disabled={subscribing} className="footer-newsletter-btn">
                  {subscribing ? "..." : "Subscribe"}
                </button>
              </form>
            </div>

            {/* Social icons */}
            <div className="mt-6 flex gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="footer-social" style={{ "--social-bg": "rgba(10,102,194,0.10)", "--social-border": "rgba(10,102,194,0.22)" }}>
                <svg className="h-4 w-4" style={{ color: "#0a66c2" }} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="footer-social" style={{ "--social-bg": "rgba(29,155,240,0.10)", "--social-border": "rgba(29,155,240,0.22)" }}>
                <svg className="h-4 w-4" style={{ color: "#1d9bf0" }} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              <a href="mailto:support@vistaarhms.com" className="footer-social" style={{ "--social-bg": "rgba(99,102,241,0.10)", "--social-border": "rgba(99,102,241,0.22)" }}>
                <svg className="h-4 w-4" style={{ color: "#6366f1" }} viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z" /></svg>
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Product</p>
            <nav className="space-y-3 text-sm text-slate-600">
              <Link to={allRoutes.features} className="block hover:text-slate-900 transition">Features</Link>
              <Link to={allRoutes.pricing} className="block hover:text-slate-900 transition">Pricing</Link>
              <Link to={allRoutes.integrations} className="block hover:text-slate-900 transition">Integrations</Link>
              <Link to={allRoutes.login} className="block hover:text-slate-900 transition">Login</Link>
            </nav>
          </div>

          {/* Company */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Company</p>
            <nav className="space-y-3 text-sm text-slate-600">
              <Link to={allRoutes.about} className="block hover:text-slate-900 transition">About</Link>
              <Link to={allRoutes.contact} className="block hover:text-slate-900 transition">Contact</Link>
              <Link to={allRoutes.support} className="block hover:text-slate-900 transition">Support</Link>
              <Link to={allRoutes.support} className="block hover:text-slate-900 transition">FAQ</Link>
            </nav>
          </div>

          {/* Legal */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Legal</p>
            <nav className="space-y-3 text-sm text-slate-600">
              <Link to={allRoutes.privacy} className="block hover:text-slate-900 transition">Privacy Policy</Link>
              <Link to={allRoutes.terms} className="block hover:text-slate-900 transition">Terms of Service</Link>
              <Link to={allRoutes.security} className="block hover:text-slate-900 transition">Security</Link>
              <Link to={allRoutes.privacy} className="block hover:text-slate-900 transition">Data Protection</Link>
            </nav>
          </div>

        </div>

        <div className="glow-divider mb-6"></div>

        {/* Bottom: copyright + trust badge chips */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500 m-0">&copy; {year} Vistaar. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="footer-trust-chip">
              <span className="footer-trust-dot" style={{ background: "#10b981" }}></span>
              Enterprise-grade security
            </span>
            <span className="footer-trust-chip">
              <span className="footer-trust-dot" style={{ background: "#6366f1" }}></span>
              AES-256 encrypted
            </span>
            <span className="footer-trust-chip">
              <span className="footer-trust-dot" style={{ background: "#0891b2" }}></span>
              Daily backups
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
